import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  XMarkIcon, 
  DocumentIcon, 
  MagnifyingGlassIcon,
  LinkIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  ShareIcon,
  FolderIcon,
  ChevronRightIcon,
  HomeIcon,
  TableCellsIcon,
  PresentationChartBarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const GoogleDriveIcon = (props) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    x="0px" 
    y="0px" 
    width="24" 
    height="24" 
    viewBox="4 6 40 36"
    {...props}
  >
    <path fill="#1e88e5" d="M38.59,39c-0.535,0.93-0.298,1.68-1.195,2.197C36.498,41.715,35.465,42,34.39,42H13.61 c-1.074,0-2.106-0.285-3.004-0.802C9.708,40.681,9.945,39.93,9.41,39l7.67-9h13.84L38.59,39z"></path>
    <path fill="#fbc02d" d="M27.463,6.999c1.073-0.002,2.104-0.716,3.001-0.198c0.897,0.519,1.66,1.27,2.197,2.201l10.39,17.996 c0.537,0.93,0.807,1.967,0.808,3.002c0.001,1.037-1.267,2.073-1.806,3.001l-11.127-3.005l-6.924-11.993L27.463,6.999z"></path>
    <path fill="#e53935" d="M43.86,30c0,1.04-0.27,2.07-0.81,3l-3.67,6.35c-0.53,0.78-1.21,1.4-1.99,1.85L30.92,30H43.86z"></path>
    <path fill="#4caf50" d="M5.947,33.001c-0.538-0.928-1.806-1.964-1.806-3c0.001-1.036,0.27-2.073,0.808-3.004l10.39-17.996 c0.537-0.93,1.3-1.682,2.196-2.2c0.897-0.519,1.929,0.195,3.002,0.197l3.459,11.009l-6.922,11.989L5.947,33.001z"></path>
    <path fill="#1565c0" d="M17.08,30l-6.47,11.2c-0.78-0.45-1.46-1.07-1.99-1.85L4.95,33c-0.54-0.93-0.81-1.96-0.81-3H17.08z"></path>
    <path fill="#2e7d32" d="M30.46,6.8L24,18L17.53,6.8c0.78-0.45,1.66-0.73,2.6-0.79L27.46,6C28.54,6,29.57,6.28,30.46,6.8z"></path>
  </svg>
);

function getFileIcon(doc) {
  if (doc.isFolder) return { Icon: FolderIcon, color: 'text-amber-500' };
  const mime = doc.mimeType || '';
  if (mime.includes('spreadsheet') || mime.includes('csv') || mime.includes('excel'))
    return { Icon: TableCellsIcon, color: 'text-green-600' };
  if (mime.includes('presentation') || mime.includes('powerpoint'))
    return { Icon: PresentationChartBarIcon, color: 'text-orange-500' };
  if (mime.includes('document') || mime.includes('text') || mime.includes('pdf'))
    return { Icon: DocumentTextIcon, color: 'text-blue-600' };
  return { Icon: DocumentIcon, color: 'text-gray-500' };
}

const GoogleDriveModal = ({ isOpen, onClose, onDocumentSelect }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [recentDocs, setRecentDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [activeTab, setActiveTab] = useState('recent');
  const [, setConnectionStatus] = useState(null);
  const [shareableLinkLoading, setShareableLinkLoading] = useState(null);
  const [folderStack, setFolderStack] = useState([]);

  useEffect(() => {
    if (isOpen) {
      checkConnectionStatus();
      setFolderStack([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isConnected && activeTab === 'recent') {
      if (folderStack.length === 0) {
        fetchRecentDocs();
      } else {
        fetchFolder(folderStack[folderStack.length - 1].id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, activeTab, folderStack.length]);

  const checkConnectionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/google-drive/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setIsConnected(response.data.connected);
        setConnectionStatus(response.data);
      }
    } catch (error) {
      console.error('Error checking Google Drive status:', error);
      setIsConnected(false);
    }
  };

  const connectGoogleDrive = async () => {
    try {
      setIsConnecting(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/google-drive/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.authUrl) {
        const authWindow = window.open(response.data.authUrl, 'google-drive-auth', 'width=600,height=600,scrollbars=yes,resizable=yes');
        const pollInterval = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(pollInterval);
            setIsConnecting(false);
            setTimeout(checkConnectionStatus, 1000);
          }
        }, 1000);
        setTimeout(() => {
          if (authWindow && !authWindow.closed) {
            authWindow.close();
            clearInterval(pollInterval);
            setIsConnecting(false);
            toast.error('Google Drive connection timed out');
          }
        }, 300000);
      }
    } catch (error) {
      console.error('Error connecting Google Drive:', error);
      setIsConnecting(false);
      toast.error('Failed to connect Google Drive');
    }
  };

  const disconnectGoogleDrive = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/google-drive/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsConnected(false);
      setRecentDocs([]);
      setConnectionStatus(null);
      setFolderStack([]);
      toast.success('Google Drive disconnected');
    } catch (error) {
      console.error('Error disconnecting Google Drive:', error);
      toast.error('Failed to disconnect Google Drive');
    }
  };

  const fetchRecentDocs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/google-drive/recent-docs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setRecentDocs(response.data.documents);
      }
    } catch (error) {
      console.error('Error fetching recent docs:', error);
      if (error.response?.status === 401) {
        setIsConnected(false);
        toast.error('Google Drive connection expired. Please reconnect.');
      } else {
        toast.error(error.response?.data?.details || error.response?.data?.error || 'Failed to fetch recent documents');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFolder = useCallback(async (folderId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/google-drive/folder/${folderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setRecentDocs(response.data.documents);
      }
    } catch (error) {
      console.error('Error fetching folder:', error);
      toast.error(error.response?.data?.details || error.response?.data?.error || 'Failed to load folder');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFolderClick = (folder) => {
    setFolderStack(prev => [...prev, { id: folder.id, name: folder.name }]);
  };

  const handleBreadcrumbClick = (index) => {
    if (index === -1) {
      setFolderStack([]);
    } else {
      setFolderStack(prev => prev.slice(0, index + 1));
    }
  };

  const handleDocumentClick = async (doc) => {
    if (doc.isFolder) {
      handleFolderClick(doc);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/google-drive/get-doc`, {
        docId: doc.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        onDocumentSelect({
          type: 'google-drive',
          document: response.data.document,
          content: response.data.document.content,
          name: response.data.document.name,
          url: response.data.document.webViewLink
        });
        onClose();
        toast.success(`Added "${response.data.document.name}" to chat`);
      }
    } catch (error) {
      console.error('Error fetching document content:', error);
      const msg = error.response?.data?.error || error.response?.data?.details || 'Failed to fetch document content';
      toast.error(msg);
    }
  };

  const handleGetShareableLink = async (e, doc) => {
    e.stopPropagation();
    if (doc.isFolder) return;
    try {
      setShareableLinkLoading(doc.id);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/google-drive/shareable-link`, {
        fileId: doc.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.shareableLink) {
        await navigator.clipboard.writeText(response.data.shareableLink);
        toast.success('Link copied to clipboard');
      }
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to get shareable link';
      toast.error(msg);
    } finally {
      setShareableLinkLoading(null);
    }
  };

  const handleUrlSubmit = async () => {
    if (!urlInput.trim()) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/auth/google-drive/get-doc`, {
        url: urlInput.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        onDocumentSelect({
          type: 'google-drive',
          document: response.data.document,
          content: response.data.document.content,
          name: response.data.document.name,
          url: response.data.document.webViewLink
        });
        onClose();
        setUrlInput('');
        toast.success(`Added "${response.data.document.name}" to chat`);
      }
    } catch (error) {
      console.error('Error fetching document from URL:', error);
      const msg = error.response?.data?.error || error.response?.data?.details;
      if (error.response?.status === 400) {
        toast.error(msg || 'Invalid Google Docs/Sheets/Slides URL');
      } else if (error.response?.status === 404) {
        toast.error(msg || 'Document not found or you do not have access');
      } else if (error.response?.status === 403) {
        toast.error(msg || 'You do not have permission to access this document');
      } else {
        toast.error(msg || 'Failed to fetch document');
      }
    }
  };

  const filteredDocs = recentDocs.filter(doc =>
    doc.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="relative w-full max-w-2xl bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-xl border border-gray-200 dark:border-[#2a2a2a] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-[#444444]">
          <div className="flex items-center">
            <GoogleDriveIcon className="w-6 h-6 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Google Drive
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(80vh-140px)]">
          {!isConnected ? (
            <div className="text-center py-8">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Connect your Google Drive
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                Access your Google Docs to add them to your conversations. 
                We'll only access documents you explicitly choose to share.
              </p>
              <button
                onClick={connectGoogleDrive}
                disabled={isConnecting}
                className="inline-flex items-center px-6 py-3 bg-black dark:bg-white hover:bg-[#181818] dark:hover:bg-[#c3c3c3] text-white dark:text-black font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isConnecting ? (
                  <>
                    <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <GoogleDriveIcon className="w-5 h-5 mr-2" />
                    Connect Google Drive
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Connection Status */}
              <div className="border border-gray-200 dark:border-[#282828] rounded-xl bg-white dark:bg-[#181818]">
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CheckCircleIcon className="w-4 h-4 text-green-600 dark:text-green-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Google Drive Connected
                      </span>
                    </div>
                    <button
                      onClick={disconnectGoogleDrive}
                      className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                    >
                      Disconnect
                    </button>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="border border-gray-200 dark:border-[#282828] rounded-xl bg-white dark:bg-[#181818]">
                <div className="flex border-b border-gray-200 dark:border-[#282828]">
                  <button
                    onClick={() => { setActiveTab('recent'); setFolderStack([]); }}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === 'recent'
                        ? 'text-gray-900 dark:text-white border-b-2 border-black dark:border-white'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                    }`}
                  >
                    {folderStack.length > 0 ? 'Browse' : 'Recent Files'}
                  </button>
                  <button
                    onClick={() => setActiveTab('url')}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === 'url'
                        ? 'text-gray-900 dark:text-white border-b-2 border-black dark:border-white'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                    }`}
                  >
                    Add by URL
                  </button>
                </div>

                <div className="p-4">
                  {activeTab === 'recent' && (
                    <div className="space-y-3">
                      {/* Breadcrumbs */}
                      {folderStack.length > 0 && (
                        <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 overflow-x-auto pb-1">
                          <button
                            onClick={() => handleBreadcrumbClick(-1)}
                            className="flex items-center hover:text-gray-900 dark:hover:text-white transition-colors flex-shrink-0"
                          >
                            <HomeIcon className="w-4 h-4" />
                          </button>
                          {folderStack.map((folder, i) => (
                            <React.Fragment key={folder.id}>
                              <ChevronRightIcon className="w-3 h-3 flex-shrink-0" />
                              <button
                                onClick={() => handleBreadcrumbClick(i)}
                                className={`truncate max-w-[120px] hover:text-gray-900 dark:hover:text-white transition-colors ${
                                  i === folderStack.length - 1 ? 'text-gray-900 dark:text-white font-medium' : ''
                                }`}
                              >
                                {folder.name}
                              </button>
                            </React.Fragment>
                          ))}
                        </div>
                      )}

                      {/* Search */}
                      <div className="relative">
                        <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search files..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-9 pr-4 py-2 border border-gray-200 dark:border-[#282828] rounded-xl bg-gray-50 dark:bg-[#0f0f0f] text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent text-sm"
                        />
                      </div>

                      {/* File List */}
                      <div className="max-h-80 overflow-y-auto space-y-1">
                        {loading ? (
                          <div className="flex items-center justify-center py-8">
                            <ArrowPathIcon className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                            <span className="text-sm text-gray-600 dark:text-gray-400">Loading...</span>
                          </div>
                        ) : filteredDocs.length === 0 ? (
                          <div className="text-center py-8">
                            <DocumentIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {searchTerm ? 'No files found' : folderStack.length > 0 ? 'This folder is empty' : 'No recent files'}
                            </p>
                          </div>
                        ) : (
                          filteredDocs.map((doc) => {
                            const { Icon: FileIcon, color } = getFileIcon(doc);
                            return (
                              <div
                                key={doc.id}
                                className="w-full text-left p-2.5 border border-gray-200 dark:border-[#282828] rounded-xl hover:bg-gray-50 dark:hover:bg-[#0f0f0f] transition-colors flex items-center gap-2 cursor-pointer"
                              >
                                <button
                                  onClick={() => handleDocumentClick(doc)}
                                  className="flex-1 flex items-center min-w-0"
                                >
                                  <FileIcon className={`w-5 h-5 mr-3 flex-shrink-0 ${color}`} />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                      {doc.name}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      {doc.isFolder
                                        ? 'Folder'
                                        : `${doc.fileType || ''}${doc.modifiedTime ? ` \u00b7 ${formatDate(doc.modifiedTime)}` : ''}`}
                                    </p>
                                  </div>
                                </button>
                                {!doc.isFolder && (
                                  <button
                                    onClick={(e) => handleGetShareableLink(e, doc)}
                                    disabled={shareableLinkLoading === doc.id}
                                    className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                                    title="Copy link"
                                  >
                                    {shareableLinkLoading === doc.id ? (
                                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <ShareIcon className="w-4 h-4" />
                                    )}
                                  </button>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab === 'url' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Google Docs URL
                        </label>
                        <div className="flex">
                          <div className="relative flex-1">
                            <LinkIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                              type="url"
                              placeholder="https://docs.google.com/document/d/... or spreadsheet or presentation URL"
                              value={urlInput}
                              onChange={(e) => setUrlInput(e.target.value)}
                              className="w-full pl-9 pr-4 py-2 border border-gray-200 dark:border-[#282828] rounded-l-xl bg-gray-50 dark:bg-[#0f0f0f] text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent text-sm"
                            />
                          </div>
                          <button
                            onClick={handleUrlSubmit}
                            disabled={!urlInput.trim()}
                            className="px-4 py-2 bg-black dark:bg-white hover:bg-gray-100 dark:hover:bg-gray-700 text-white dark:text-black font-medium rounded-r-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                          >
                            Add
                          </button>
                        </div>
                      </div>

                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                        <div className="flex items-start">
                          <InformationCircleIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2 mt-0.5 flex-shrink-0" />
                          <div className="text-sm text-blue-700 dark:text-blue-300">
                            <p className="font-medium mb-1">Important Notes</p>
                            <ul className="list-disc list-inside space-y-1 text-xs">
                              <li>Google Docs, Sheets, and Presentations are supported</li>
                              <li>You must have view access to the file</li>
                              <li>Text content will be extracted (images and formatting excluded)</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default GoogleDriveModal;
